import React, { useState } from 'react';
import { ReactElement } from 'react';
import { Sidebar, SidebarSection, Button } from 'bench-internal-theme';
import {
  ClientRulesLayout,
  ContractorInput,
  FormFields,
  InlineInputLayout,
  EditContractorWrapper,
  EditContractorErrorWrapper,
  EditContractorError,
  FormActions,
} from './EditContractorSidebar.styles';
import { colors } from 'bench-style-constants';

type UpdateContractorVariables = { id: string; name: string };

type EditContractorSidebarProps = {
  contractorData: { name: string; id: string };
  contractorList: { name: string; id: string }[];
  dismiss: () => void;
  handleCreateorUpdateContractor: ({ id, name }: UpdateContractorVariables) => Promise<unknown>;
};

const EditContractorSidebar = ({
  contractorData,
  contractorList,
  handleCreateorUpdateContractor,
  dismiss,
}: EditContractorSidebarProps): ReactElement => {
  const [contractorName, setContractorName] = useState(contractorData.name || '');
  const [showEditContractorError, setShowEditContractorError] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleUpdateContractor = updatedContractorName => {
    if (!contractorData?.id) return;
    setSubmitted(true);
    handleCreateorUpdateContractor({
      id: contractorData.id,
      name: updatedContractorName,
    }).finally(() => {
      setSubmitted(false);
      dismiss();
    });
  };

  return (
    <Sidebar dismiss={dismiss} headerContent="Edit Contractors">
      <ClientRulesLayout>
        <FormFields>
          <SidebarSection title="Contractor" hasBorderBottom background={colors.pearl}>
            <InlineInputLayout>
              <EditContractorWrapper>
                <ContractorInput
                  data-test-id="edit-contractor-name"
                  value={contractorName}
                  type="text"
                  onChange={event => {
                    const newValue = event.target.value;
                    setContractorName(event.target.value);
                    const contractorNames = contractorList.reduce((names: string[], contractor) => {
                      if (contractor.name !== contractorData.name) {
                        names.push(contractor.name.toLowerCase());
                      }
                      return names;
                    }, []);
                    setShowEditContractorError(contractorNames.includes(newValue.toLowerCase()));
                  }}
                  placeholder={contractorData.name}
                  color={showEditContractorError ? '#d6382c' : undefined}
                  aria-label={'Edit Contractor Input'}
                />
              </EditContractorWrapper>
              <EditContractorErrorWrapper aria-label="Edit Contractor Error">
                {showEditContractorError && (
                  <EditContractorError>
                    You already have an entry with this name
                  </EditContractorError>
                )}
              </EditContractorErrorWrapper>
            </InlineInputLayout>
          </SidebarSection>
        </FormFields>
        <FormActions>
          <Button
            data-test-id="contractor-list-cancel"
            size="large"
            styleType="ghost"
            type="button"
            onClick={dismiss}
          >
            Cancel
          </Button>
          <Button
            size="large"
            styleType="primary"
            type="button"
            onClick={() => handleUpdateContractor(contractorName)}
            disabled={
              submitted ||
              !contractorName ||
              !contractorData?.id ||
              showEditContractorError ||
              contractorName === contractorData.name
            }
            aria-label={'Save Edit Contractor'}
          >
            Save
          </Button>
        </FormActions>
      </ClientRulesLayout>
    </Sidebar>
  );
};

export default EditContractorSidebar;
